import { PROJECT_ICONS } from "@/constants/ProjectIcons";
import { ROUTES } from "@/constants/Routes";
import type { MaterialIconType } from "@/types/MaterialIcons";
import type { AbilityDescriptor } from "@/lib/casl/types";

type BaseLinkProps = {
	title: string;
	value: string;
	can: AbilityDescriptor | "all" | null;
};

export type SideSublinkProps = BaseLinkProps & {
	link: `/${string}`;
};

export type SidelinkProps = BaseLinkProps & {
	Icon: MaterialIconType;
	link?: `/${string}`;
	subItems?: SideSublinkProps[];
};

const Top: SidelinkProps[] = [
	{
		title: "Início",
		Icon: PROJECT_ICONS.ENTITY.HOME,
		link: ROUTES.HOME,
		value: "home",
		can: "all",
	},
	{
		title: "Glossário",
		Icon: PROJECT_ICONS.ENTITY.GLOSSARY,
		value: "glossary",
		can: null,
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.GLOSSARY.CONSULT,
				value: "glossary-consult",
				can: null,
			},
			{
				title: "Registrar",
				link: ROUTES.GLOSSARY.REGISTER,
				value: "glossary-register",
				can: null,
			},
			{
				title: "Editar",
				link: ROUTES.GLOSSARY.EDIT,
				value: "glossary-edit",
				can: null,
			},
		],
	},
	{
		title: "Princípios",
		Icon: PROJECT_ICONS.ENTITY.PRINCIPLES,
		value: "principles",
		can: { action: "read", subject: "Principle" },
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.PRINCIPLE.CONSULT,
				value: "principle-consult",
				can: "all",
			},
			{
				title: "Registrar",
				link: ROUTES.PRINCIPLE.REGISTER,
				value: "principle-register",
				can: { action: "create", subject: "Principle" },
			},
			{
				title: "Editar",
				link: ROUTES.PRINCIPLE.EDIT,
				value: "principle-edit",
				can: { action: "update", subject: "Principle" },
			},
		],
	},
	{
		title: "AIA",
		Icon: PROJECT_ICONS.ENTITY.AIA,
		value: "aia",
		can: { action: "read", subject: "AIA" },
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.AIA.CONSULT,
				value: "aia-consult",
				can: { action: "read", subject: "AIA" },
			},
			{
				title: "Registrar",
				link: ROUTES.AIA.REGISTER,
				value: "aia-register",
				can: { action: "create", subject: "AIA" },
			},
			{
				title: "Editar",
				link: ROUTES.AIA.EDIT,
				value: "aia-edit",
				can: { action: "update", subject: "AIA" },
			},
		],
	},
	{
		title: "EPS",
		Icon: PROJECT_ICONS.ENTITY.EPS,
		value: "eps",
		can: { action: "read", subject: "EPS" },
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.EPS.CONSULT,
				value: "eps-consult",
				can: { action: "read", subject: "EPS" },
			},
			{
				title: "Registrar",
				link: ROUTES.EPS.REGISTER,
				value: "eps-register",
				can: { action: "create", subject: "EPS" },
			},
			{
				title: "Editar",
				link: ROUTES.EPS.EDIT,
				value: "eps-edit",
				can: { action: "update", subject: "EPS" },
			},
		],
	},
	{
		title: "Usuários",
		Icon: PROJECT_ICONS.ENTITY.USERS,
		value: "user",
		can: { action: "read", subject: "User" },
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.USER.CONSULT,
				value: "user-consult",
				can: { action: "read", subject: "User" },
			},
			{
				title: "Registrar",
				link: ROUTES.USER.REGISTER,
				value: "user-register",
				can: { action: "create", subject: "User" },
			},
			{
				title: "Editar",
				link: ROUTES.USER.EDIT,
				value: "user-edit",
				can: { action: "update", subject: "User" },
			},
		],
	},
	{
		title: "Organizações",
		Icon: PROJECT_ICONS.ENTITY.ORGANIZATION,
		value: "organization",
		can: "all",
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.ORGANIZATION.CONSULT,
				value: "organization-consult",
				can: { action: "read", subject: "Organization" },
			},
		],
	},
	{
		title: "Aplicações",
		Icon: PROJECT_ICONS.ENTITY.APPLICATION,
		value: "application",
		can: { action: "read", subject: "Application" },
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.APPLICATION.CONSULT,
				value: "application-consult",
				can: { action: "read", subject: "Application" },
			},
			{
				title: "Avaliar",
				link: `${ROUTES.APPLICATION.EVALUATE}?page=0`,
				value: "application-evaluate",
				can: { action: "evaluate", subject: "Application" },
			},
			{
				title: "Deliberar",
				link: `${ROUTES.APPLICATION.DELIBERATE}?page=0`,
				value: "application-deliberate",
				can: { action: "deliberate", subject: "Application" },
			},
		],
	},
];

const Editor: SidelinkProps[] = [
	{
		title: "Questionários",
		Icon: PROJECT_ICONS.ENTITY.QUESTIONNAIRES,
		value: "questionnaires",
		can: {
			action: "create",
			subject: ["AIAQuestionnaire", "EPSQuestionnaire"],
		},
		subItems: [
			{
				title: "Registrar (AIA)",
				link: ROUTES.QUESTIONNAIRE.REGISTER.AIA,
				value: "questionnaire-aia-register",
				can: { action: "create", subject: "AIAQuestionnaire" },
			},
			{
				title: "Registrar (EPS)",
				link: ROUTES.QUESTIONNAIRE.REGISTER.EPS,
				value: "questionnaire-eps-register",
				can: { action: "create", subject: "EPSQuestionnaire" },
			},
		],
	},
	{
		title: "Áreas",
		Icon: PROJECT_ICONS.ENTITY.AREAS,
		value: "areas",
		can: { action: "read", subject: "Area" },
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.AREA.CONSULT,
				value: "area-consult",
				can: { action: "read", subject: "Area" },
			},
			{
				title: "Registrar",
				link: ROUTES.AREA.REGISTER,
				value: "area-register",
				can: { action: "create", subject: "Area" },
			},
			{
				title: "Editar",
				link: ROUTES.AREA.EDIT,
				value: "area-edit",
				can: { action: "update", subject: "Area" },
			},
		],
	},
	{
		title: "Seções",
		Icon: PROJECT_ICONS.ENTITY.SECTIONS,
		value: "sections",
		can: { action: "read", subject: "Section" },
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.SECTION.CONSULT,
				value: "section-consult",
				can: { action: "read", subject: "Section" },
			},
			{
				title: "Registrar",
				link: ROUTES.SECTION.REGISTER,
				value: "section-register",
				can: { action: "create", subject: "Section" },
			},
			{
				title: "Editar",
				link: ROUTES.SECTION.EDIT,
				value: "section-edit",
				can: { action: "update", subject: "Section" },
			},
		],
	},
	{
		title: "Perguntas",
		Icon: PROJECT_ICONS.ENTITY.QUESTIONS,
		value: "questions",
		can: { action: "read", subject: "Question" },
		subItems: [
			{
				title: "Consultar",
				link: ROUTES.QUESTION.CONSULT,
				value: "question-consult",
				can: { action: "read", subject: "Question" },
			},
			{
				title: "Registrar",
				link: ROUTES.QUESTION.REGISTER,
				value: "question-register",
				can: { action: "create", subject: "Question" },
			},
			{
				title: "Editar",
				link: ROUTES.QUESTION.EDIT,
				value: "question-edit",
				can: { action: "update", subject: "Question" },
			},
		],
	},
];

const Bottom: SidelinkProps[] = [
	{
		title: "Termos e Condições",
		Icon: PROJECT_ICONS.ENTITY.TERMS_AND_CONDITIONS,
		link: ROUTES.TERMS_AND_CONDITIONS,
		value: "terms-and-conditions",
		can: "all",
	},
	{
		title: "Política de Privacidade",
		Icon: PROJECT_ICONS.ENTITY.PRIVACY_POLITICS,
		link: ROUTES.PRIVACY_POLITICS,
		value: "privacy-policies",
		can: "all",
	},
];

export const Config = {
	Top,
	Editor,
	Bottom,
};
